import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  CandidateProcedure,
  CandidateProcedureDecision,
  CandidateVerification,
  CandidateVerificationHistory,
  NewCandidateVerificationOpts,
  PaginatedResponse,
  SearchCandidateVerificationsOpts,
  SmsSendResult,
  UpdateCandidateVerificationDecisionOpts,
  UpdateCandidateVerificationDetailsOpts,
  UserProcess,
} from '@interfaces';
import { environment } from '@environment';
import { ManagerOfCompany, SearchProcessesOfManagedEmployees } from 'src/app/_interfaces/manager';

@Injectable({
  providedIn: 'root',
})
export class ManagerHttpService {
  private readonly http = inject(HttpClient);

  private API_URL = environment.API_URL;
  private defaultOpts = { withCredentials: true };

  public getMyManagedCompanies(): Observable<ManagerOfCompany[]> {
    const url = `${this.API_URL}/iammngr/companies`;
    return this.http.get<ManagerOfCompany[]>(url);
  }

  public searchProcessesOfManagedEmployees(
    payload: SearchProcessesOfManagedEmployees
  ): Observable<PaginatedResponse<Partial<UserProcess>>> {
    const url = `${this.API_URL}/iammngr/processes/search`;
    return this.http.post<PaginatedResponse<Partial<UserProcess>>>(url, payload);
  }

  /**
   * ============================================================ CANDIDATE VERIFICATION
   * Only For Manager Role
   */
  public searchCandidatesVerifications(
    opts: SearchCandidateVerificationsOpts
  ): Observable<PaginatedResponse<Partial<CandidateVerification>>> {
    const url = `${this.API_URL}/candidate-verification/search`;
    return this.http.post<PaginatedResponse<Partial<CandidateVerification>>>(url, opts);
  }

  public newCandidateVerification(
    opts: NewCandidateVerificationOpts
  ): Observable<{ candidateVerification: Partial<CandidateVerification>; smsResult: SmsSendResult }> {
    const url = `${this.API_URL}/candidate-verification/new`;
    return this.http.post<{ candidateVerification: Partial<CandidateVerification>; smsResult: SmsSendResult }>(
      url,
      opts
    );
  }

  public getCandidateVerificationDetails(candidateVerificationId: string): Observable<CandidateVerification> {
    const url = `${this.API_URL}/candidate-verification/${candidateVerificationId}/details`;
    return this.http.get<CandidateVerification>(url);
  }

  public getCandidateVerificationHistory(candidateVerificationId: string): Observable<CandidateVerificationHistory[]> {
    const url = `${this.API_URL}/candidate-verification/${candidateVerificationId}/history`;
    return this.http.get<CandidateVerificationHistory[]>(url);
  }

  public updateCandidateVerificationDetails(
    candidateVerificationId: string,
    opts: UpdateCandidateVerificationDetailsOpts
  ): Observable<{ candidateVerification: Partial<CandidateVerification>; smsResult?: SmsSendResult | null }> {
    const url = `${this.API_URL}/candidate-verification/${candidateVerificationId}/update-basic-details`;
    return this.http.patch<{ candidateVerification: Partial<CandidateVerification>; smsResult?: SmsSendResult | null }>(
      url,
      opts
    );
  }

  public regenerateFormLinkAndPin(candidateVerificationId: string): Observable<{ smsResult: SmsSendResult }> {
    const url = `${this.API_URL}/candidate-verification/${candidateVerificationId}/regenerate-pin-and-link`;
    return this.http.get<{ smsResult: SmsSendResult }>(url);
  }

  public setCandidateVerificationDecision(
    candidateVerificationId: string,
    opts: UpdateCandidateVerificationDecisionOpts
  ): Observable<{ candidateVerification: Partial<CandidateVerification> }> {
    const url = `${this.API_URL}/candidate-verification/${candidateVerificationId}/decision`;

    return this.http.patch<{ candidateVerification: Partial<CandidateVerification> }>(url, opts, this.defaultOpts);
  }

  public updateCandidateVerificationProcedureDecision(
    candidateVerificationId: string,
    procedureId: string,
    decision: CandidateProcedureDecision
  ): Observable<void> {
    const url = `${this.API_URL}/candidate-verification/${candidateVerificationId}/procedures/${procedureId}/decision`;

    return this.http.patch<void>(url, { decision }, this.defaultOpts);
  }

  public addCandidateOptionalServices(
    candidateVerificationId: string,
    services: CandidateProcedure['type'][]
  ): Observable<CandidateProcedure[]> {
    const url = `${this.API_URL}/candidate-verification/${candidateVerificationId}/procedures/extra-services`;

    return this.http.post<CandidateProcedure[]>(url, { services }, this.defaultOpts);
  }

  /**
   * Only For Manager Role
   * ============================================================ CANDIDATE VERIFICATION END
   */
}
